import { useState, useRef, useEffect, useCallback } from 'react';

const TOP_LAYER_VAR = '--wc-country-top-layer-height';

// El scroll real en mobile/tablet ocurre dentro de .content_div (overflow:
// scroll), no en el viewport. En iOS Safari los scroll events de un elemento
// overflow:scroll no propagan por la cadena de captura hasta window, por lo
// que escuchar window.scroll no es suficiente. Detectamos el contenedor
// scrollable real y le enganchamos el listener directamente.
const findScrollContainer = el => {
  if (!el || typeof window === 'undefined') return null;
  let node = el.parentElement;
  while (node && node !== document.body) {
    const cs = getComputedStyle(node);
    const overflowY = cs.overflowY || cs.overflow;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

export const useSubTabsStuck = ({ scopeRef } = {}) => {
  const wrapperRef = useRef(null);
  // Sentinel = nodo height:0 que va INMEDIATAMENTE antes del wrapper en el
  // mismo padre. Cuando el wrapper se queda sticky, el sentinel sigue
  // scrolleando hacia arriba y queda por encima del wrapper. Comparando
  // sus rects detectamos el stuck sin depender del valor calc() del top:
  // del CSS (que cambia con --wc-tabs-height, --wc-country-top-layer-height
  // y env(safe-area-inset-top) según breakpoint, y arrastra cualquier
  // desalineación entre fórmula JS y CSS real).
  const sentinelRef = useRef(null);
  // El nodo del top-layer entra como callback ref para que el efecto se
  // reconstruya cuando el CountryHeader monta tarde (teamCard async) y
  // exponer su altura vía --wc-country-top-layer-height para el CSS.
  const [topLayerNode, setTopLayerNode] = useState(null);
  const [isStuck, setIsStuck] = useState(false);

  const topLayerRef = useCallback(node => {
    setTopLayerNode(node || null);
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const sentinel = sentinelRef.current;
    if (!wrapper || !sentinel) return undefined;

    const update = () => {
      const layerH = topLayerNode ? topLayerNode.offsetHeight : 0;
      const scope =
        (scopeRef && scopeRef.current) || document.documentElement;
      scope.style.setProperty(TOP_LAYER_VAR, `${layerH}px`);

      const sentinelTop = sentinel.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      // Sin sticky activo: sentinelTop ≈ wrapperTop (el sentinel está
      // pegado al wrapper). Con sticky activo: el wrapper se ancla al
      // viewport y el sentinel sigue subiendo → sentinelTop < wrapperTop.
      setIsStuck(sentinelTop < wrapperTop - 0.5);
    };

    // Detectamos el contenedor real para soportar cambios de overflow por
    // breakpoint y evitar quedar escuchando al scroller viejo.
    let currentScrollTarget = null;
    let removeScrollListener = () => {};

    const syncScroller = () => {
      const scroller = findScrollContainer(wrapper);
      const newTarget = scroller || window;
      if (newTarget !== currentScrollTarget) {
        removeScrollListener();
        currentScrollTarget = newTarget;
        newTarget.addEventListener('scroll', update, { passive: true });
        removeScrollListener = () => newTarget.removeEventListener('scroll', update);
      }
    };

    // Usamos setTimeout en lugar de un solo rAF porque durante un resize o
    // cambio de orientación el browser puede tardar varios frames en asentar
    // los estilos CSS (media queries, overflow). Con rAF getComputedStyle
    // podría devolver valores del layout anterior y syncScroller terminaría
    // escuchando al contenedor equivocado — que es exactamente el bug que
    // hace que data-stuck no se vuelva a poner al scrollear.
    let resizeTimerId = 0;
    const handleResize = () => {
      clearTimeout(resizeTimerId);
      resizeTimerId = setTimeout(() => {
        syncScroller();
        update();
      }, 200);
    };

    syncScroller();
    update();

    let resizeObserver = null;
    if (topLayerNode && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => requestAnimationFrame(update));
      resizeObserver.observe(topLayerNode);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimerId);
      removeScrollListener();
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
      const scope =
        (scopeRef && scopeRef.current) || document.documentElement;
      scope.style.removeProperty(TOP_LAYER_VAR);
    };
  }, [topLayerNode, scopeRef]);

  return { wrapperRef, sentinelRef, topLayerRef, isStuck };
};
